import React, { useEffect, useState } from "react";
import Modal from "components/global/modal";
import Button from "components/button/button";
import ModalHeader from "components/global/modal-header";
import FileUpload from "components/file-upload/file-upload";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const headers = {
  headers: {
    "content-type": "multipart/form-data",
  },
  mode: "no-cors",
};

export default function EditFeaturedProfileModal({ hide, ...props }) {
  const { http } = global.services;
  const editId = props?.id || props?.user_profile_selection_id || props?.selection_id || props?.user_profile?.selection_id;
  if (!editId) {
    // Try last resort fallback to record id
    // Avoid throwing; UI still renders with read-only info and save will fail clearly if id missing
  }
  const [state, setState] = useState({ image: null });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [rawImageUrl, setRawImageUrl] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState();
  const imgRef = React.useRef(null);
  const previewCanvasRef = React.useRef(null);

  const existingImageUrl = props?.media?.webp_url || props?.media?.url || props?.user_profile?.cover_image_url || null;

  const onUploadImage = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setState((prev) => ({ ...prev, image: file }));
      const url = URL.createObjectURL(file);
      setRawImageUrl(url);
    }
  };

  // Create a high-quality cropped blob using the image's natural resolution,
  // then cap the output to a maximum of 1920x1080 while keeping 16:9.
  const getCroppedBlob = (imageEl, crop, type = "image/jpeg", quality = 1) =>
    new Promise((resolve) => {
      if (!imageEl || !crop?.width || !crop?.height) return resolve(null);

      const scaleX = imageEl.naturalWidth / imageEl.width;
      const scaleY = imageEl.naturalHeight / imageEl.height;

      const sx = Math.round(crop.x * scaleX);
      const sy = Math.round(crop.y * scaleY);
      const sWidth = Math.round(crop.width * scaleX);
      const sHeight = Math.round(crop.height * scaleY);

      // Compute destination canvas size with max 1920x1080 cap (still 16:9)
      const MAX_W = 1920;
      const MAX_H = 1080;
      const scaleOut = Math.min(1, MAX_W / sWidth, MAX_H / sHeight);
      const dWidth = Math.max(1, Math.round(sWidth * scaleOut));
      const dHeight = Math.max(1, Math.round(sHeight * scaleOut));

      // Destination canvas
      const canvas = document.createElement("canvas");
      canvas.width = dWidth;
      canvas.height = dHeight;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.clearRect(0, 0, dWidth, dHeight);
      ctx.drawImage(imageEl, sx, sy, sWidth, sHeight, 0, 0, dWidth, dHeight);

      canvas.toBlob((blob) => resolve(blob), type, quality);
    });

  const handleSubmit = async () => {
    setIsLoading(true);
    let error = "";
    try {
      const form = new FormData();
      let blob = null;
      if (imgRef.current && completedCrop?.width && completedCrop?.height) {
        // Use natural-resolution crop for best quality output
        blob = await getCroppedBlob(imgRef.current, completedCrop, "image/jpeg", 0.92);
      }
      if (!blob && state.image) {
        // Fallback to original file if crop isn't available
        blob = state.image;
      }
      if (blob) form.append("cover_image_file", blob, "cover.jpg");
      form.append("user_profile_id", props?.user_profile_id);

      await http.post(`user-profile-selections/${editId}/image`, form, headers);
      hide("addOrUpdate");
    } catch (err) {
      const { data, status } = err || {};
      if (status === 422) error = data?.errors;
      else error = { form: data?.message || "Something went wrong" };
    } finally {
      setIsLoading(false);
      setErrors(error || {});
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const c = makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      16 / 9,
      width,
      height
    );
    const centered = centerCrop(c, width, height);
    setCrop(centered);
    // Also compute a pixel-based completed crop so that we can save without user interaction
    const completedPx = {
      unit: "px",
      x: Math.round((centered.x / 100) * width),
      y: Math.round((centered.y / 100) * height),
      width: Math.round((centered.width / 100) * width),
      height: Math.round((centered.height / 100) * height),
    };
    setCompletedCrop(completedPx);
  };

  useEffect(() => {
    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const c = completedCrop;
    if (!image || !canvas || !c?.width || !c?.height) return;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(c.width * pixelRatio);
    canvas.height = Math.floor(c.height * pixelRatio);

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      image,
      c.x * scaleX,
      c.y * scaleY,
      c.width * scaleX,
      c.height * scaleY,
      0,
      0,
      c.width,
      c.height
    );
  }, [completedCrop]);

  useEffect(() => {
    return () => {
      if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);
    };
  }, [rawImageUrl]);

  const Footer = () => (
    <div className="relative flex flex-row items-center justify-end space-x-2">
      <Button
        key="cancel-btn"
        buttonName="Cancel"
        buttonClass="bg-thBg py-3 px-9"
        buttonTextClass="text-black text-sm font-bold"
        onClick={hide}
      />
      <Button
        key="save-btn"
        buttonName="Save"
        buttonClass="bg-primary py-3 px-12"
        buttonTextClass="text-white text-sm font-bold"
        onClick={handleSubmit}
        disabled={isLoading}
      />
    </div>
  );

  const profileDisplay = props?.user_profile?.profile_name || props?.user_profile?.username || props?.user_profile_id || "Profile";

  return (
    <Modal
      footer={<Footer />}
      width="w-19/20 md:w-16/20 2md:w-11/20 3md:w-9/20 1xl:w-7/20"
      contentClassName="rounded-lg overflow-auto custom-scroll"
      wrapperClassName="overflow-visible"
      footerClass="border-t border-mutedGray"
    >
      <div className="w-full">
        <ModalHeader title="Edit Featured Profile" hide={hide} />

        <div className="pl-10 pr-7 py-3.5">
          {/* Read-only profile info */}
          <div className="w-full mt-3">
            <label className="text-sm font-semibold text-lightBlack">Profile</label>
            <div className="mt-2 p-3 bg-thBg rounded text-lightBlack">{profileDisplay}</div>
            <p className="text-xs opacity-60 mt-1">Profile cannot be changed.</p>
          </div>

          <div className="w-full mt-3">
            <FileUpload
              id="featured-profile-image-edit"
              label="Choose Image"
              title="Upload image for the featured profile (recommended 500x500px)"
              accept="image/*"
              image_url={rawImageUrl || existingImageUrl}
              uploading={false}
              onChange={onUploadImage}
              removable={false}
            />
            {errors?.image && (
              <p className="text-danger text-xs mt-1">{errors.image}</p>
            )}
          </div>

          {rawImageUrl && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm mb-2 font-semibold">Crop (16:9)</p>
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={16 / 9}
                >
                  <img ref={imgRef} src={rawImageUrl} alt="to-crop" onLoad={onImageLoad} />
                </ReactCrop>
              </div>
              <div>
                <p className="text-sm mb-2 font-semibold">Preview</p>
                <canvas
                  ref={previewCanvasRef}
                  style={{ width: "100%", maxWidth: 480, height: "auto", borderRadius: 6 }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
